import {DataFetcher, useGrpcRequest} from '@aboutyou/data-fetcher';
import {NotFoundMetaService_GetPageMetaDataLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/not_found_page/v1/meta/service.grpc.lazy';
import {GetPageMetaDataResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/not_found_page/v1/meta/service.types';
import {Head} from '@aboutyou/head-elements';
import {useTranslation} from '@aboutyou/i18n';
import {
  RouterErrorHandler,
  createPath,
  createRedirectHint,
  usePathname,
  useSearch,
} from '@aboutyou/router';
import {TrackContentPageView} from '@aboutyou/tracking';
import React, {FC} from 'react';

interface NotFoundMetaProps {
  notFoundPath?: string;
  disableMetaInformation?: boolean;
}

export const NotFoundMeta: FC<NotFoundMetaProps> = ({
  notFoundPath,
  disableMetaInformation = false,
}) => {
  /*
  The new View contains metadata and tracking. The problem is that the mobile page is not migrated yet and it has its own metadata and different tracking implementation. So, we need to keep the old behavior for the mobile.
  We can remove this check once the mobile is migrated. https://aboutyou.atlassian.net/browse/ASFE-7376
  */
  const view = disableMetaInformation ? () => null : View;

  return (
    <DataFetcher
      request={useRedirect({notFoundPath})}
      Error={RouterErrorHandler}
      View={view}
    />
  );
};

const useRedirect = ({notFoundPath}: {notFoundPath?: string}) => {
  const search = useSearch();
  const pathname = usePathname();
  const url = notFoundPath ?? createPath({pathname, search});

  return useGrpcRequest(
    NotFoundMetaService_GetPageMetaDataLazy,
    {url},
    {
      validateData: ensureRedirectData,
    },
  );
};

const ensureRedirectData = (response: GetPageMetaDataResponse) => {
  if (response.redirectUrl) {
    throw createRedirectHint(
      response.redirectUrl.url!,
      response.redirectUrl.statusCode!,
    );
  }
};

const View: FC<GetPageMetaDataResponse> = () => {
  const {t} = useTranslation();

  const title = t('Uh oh - 404 | ABOUT YOU');
  const metaDescription = t('Sorry, this page no longer exists.');

  return (
    <>
      <TrackContentPageView
        pageTitle={title}
        overridePageProps={{type: '404'}}
      />
      <Head>
        <meta name="description" content={metaDescription} />
        <meta name="robots" content="noindex, follow" />
        <title>{title}</title>
      </Head>
    </>
  );
};
